import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type V2BadgeTone } from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { TaskListResponse, TaskMineItem } from '../../lib/types/topbar.js';
import { NO_PROJECT_LABEL } from '../../lib/types/projects.js';

const PRIORITY_TONE: Record<string, V2BadgeTone> = {
  زیاد: 'danger',
  بالا: 'danger',
  متوسط: 'neutral',
  کم: 'neutral',
};

/**
 * Today's date for the "امروز" tab grouping — display-only comparison
 * against real `dueDate` values, not an authoritative calculation.
 * `systemClock.today()` is the calendar package's zoned "today".
 */
function todayBusinessDate(): string {
  return systemClock.today();
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §04 کارهای من.
 * Real backend: GET t/:slug/api/v1/projects/tasks?assignee=me[&overdue=true]
 * and PATCH .../tasks/:ref (apps/api/src/projects/hub.controller.ts,
 * commands.controller.ts) — both already-real, already-used-elsewhere
 * endpoints. "امروز" has no server-side "due today" filter, so it fetches
 * the same assignee=me list as "همه" and groups by dueDate client-side
 * (presentation only, not a re-derivation of due-date semantics).
 *
 * Every row has «مشاهده», opening the task detail page
 * (/projects/tasks/:ref) where the status is changed. The quick «انجام شد»
 * is only offered for a task already «در حال انجام»: the server's task
 * transitions are todo → in_progress → done, so completing a todo task
 * straight from here was always rejected (422). The real PATCH call remains
 * the authorization check (AGENTS.md §2.3: enforced server-side).
 */
export function MyTasksPopover({
  slug,
  open,
  onNavigate,
}: {
  slug: string;
  open: boolean;
  /** Closes the popover when a row navigates to the task detail page. */
  onNavigate?: () => void;
}) {
  const base = tenantBasePath(slug);
  const [tab, setTab] = useState<'today' | 'late' | 'all'>('today');
  const [refreshKey, setRefreshKey] = useState(0);
  const [completingRef, setCompletingRef] = useState<string | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const query = tab === 'late' ? '&overdue=true' : '';
  const list = useApiGet<TaskListResponse>(
    open ? `${base}/api/v1/projects/tasks?assignee=me&limit=30${query}` : null,
    [tab, refreshKey],
  );

  const today = todayBusinessDate();
  const visibleTasks: readonly TaskMineItem[] =
    list.status === 'ready'
      ? tab === 'today'
        ? list.data.tasks.filter((t) => t.dueDate === today)
        : list.data.tasks
      : [];

  async function markDone(ref: string) {
    setCompletingRef(ref);
    setCompleteError(null);
    try {
      await apiPatch(`${base}/api/v1/projects/tasks/${ref}`, { status: 'done' });
      setRefreshKey((k) => k + 1);
    } catch {
      setCompleteError('انجام این عملیات ممکن نبود.');
    } finally {
      setCompletingRef(null);
    }
  }

  return (
    <div>
      <div
        style={{
          padding: '12px 14px 10px',
          borderBottom: '1px solid var(--sk-divider)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>کارهای من</div>
        <div className="sk-tabs" style={{ alignSelf: 'flex-start' }}>
          <button
            className="sk-tab"
            aria-selected={tab === 'today'}
            onClick={() => setTab('today')}
          >
            امروز
          </button>
          <button className="sk-tab" aria-selected={tab === 'late'} onClick={() => setTab('late')}>
            معوق
          </button>
          <button className="sk-tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>
            همه
          </button>
        </div>
      </div>

      <div style={{ maxHeight: 300, overflowY: 'auto', padding: 6 }}>
        {list.status === 'loading' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 10 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="sk-skel" style={{ height: 44 }} />
            ))}
          </div>
        ) : null}

        {list.status === 'error' ? (
          <div style={{ padding: '20px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="sk-alert sk-alert--danger">
              <Icon name="warn" size="md" />
              <div className="sk-alert__body">
                <div className="sk-alert__title">دریافت فهرست کارها ناموفق بود</div>
                <div style={{ fontSize: 12 }}>اتصال را بررسی کنید و دوباره تلاش کنید.</div>
              </div>
            </div>
            <button
              className="sk-btn sk-btn--secondary sk-btn--sm"
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              تلاش دوباره
            </button>
          </div>
        ) : null}

        {completeError ? (
          <div style={{ padding: '0 8px 8px' }}>
            <div className="sk-alert sk-alert--danger" style={{ fontSize: 11 }}>
              {completeError}
            </div>
          </div>
        ) : null}

        {list.status === 'ready' && visibleTasks.length === 0 ? (
          <div
            style={{
              padding: '34px 16px',
              textAlign: 'center',
              fontSize: 12,
              color: 'var(--sk-fg-subtle)',
            }}
          >
            کاری برای شما ثبت نشده است.
          </div>
        ) : null}

        {visibleTasks.map((t) => (
          <div
            key={t.ref}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: 10,
              borderRadius: 10,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>{t.title}</div>
              <div style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                {t.projectRef ?? NO_PROJECT_LABEL}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  color: 'var(--sk-fg-muted)',
                }}
              >
                <span>{displayJalaliDate(t.dueDate)}</span>
                <span
                  className={`sk-badge sk-badge--${PRIORITY_TONE[t.priorityLabel] ?? 'neutral'}`}
                >
                  {t.priorityLabel}
                </span>
              </div>
            </div>
            <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Link
                to={`${base}/projects/tasks/${encodeURIComponent(t.ref)}`}
                className="sk-btn sk-btn--secondary sk-btn--sm"
                style={{ textDecoration: 'none' }}
                onClick={onNavigate}
              >
                مشاهده
              </Link>
              {t.statusLabel === 'در حال انجام' ? (
                <button
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  disabled={completingRef === t.ref}
                  onClick={() => markDone(t.ref)}
                >
                  انجام شد
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: 10, textAlign: 'center', borderTop: '1px solid var(--sk-divider)' }}>
        <Link
          to={`${base}/projects/tasks`}
          style={{ fontSize: 12, fontWeight: 500, color: 'var(--sk-brand-600)' }}
          onClick={onNavigate}
        >
          مشاهده کارتابل من
        </Link>
      </div>
    </div>
  );
}
